import { Button, ButtonTypes, Skeleton } from "@repo/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Grip, Mail, Maximize2, Settings2, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { MyViewController as Api, type MyViewWidget, type WidgetData } from "../../../api/myViewController";
import { ExportSettings } from "../settings/exportSettings";
import commonStyles from "../shared/style.module.scss";
import styles from "./style.module.scss";
import { WidgetDataView } from "./widgetData";

export function WidgetCard({
	widget,
	editing,
	onConfigure,
	onDelete,
	onPointer,
	onKey,
	refresh,
	live,
}: {
	widget: MyViewWidget;
	editing: boolean;
	onConfigure: () => void;
	onDelete: () => void;
	onPointer: (e: React.PointerEvent, resize: boolean) => void;
	onKey: (e: React.KeyboardEvent, resize: boolean) => void;
	refresh: number;
	live: boolean;
}) {
	const client = useQueryClient();
	const [exporting, setExporting] = useState(false);
	const [headerActions, setHeaderActions] = useState<HTMLDivElement | null>(null);
	const force = useRef(false);
	const queryKey = ["myViewV2", "data", widget.id, widget.timeframe, widget.configJson];
	const query = useQuery({
		queryKey,
		enabled: widget.enabled,
		queryFn: async ({ signal }) => {
			const previous = client.getQueryData<WidgetData>(queryKey);
			const result = await Api.getData(widget.id, previous?.refreshToken, force.current, signal);
			force.current = false;
			return result.unchanged && previous ? { ...previous, refreshToken: result.refreshToken } : result;
		},
		staleTime: 30_000,
		refetchInterval: live ? 60_000 : false,
		refetchIntervalInBackground: false,
		retry: 1,
	});
	const previousRefresh = useRef(refresh);
	const { refetch } = query;
	useEffect(() => {
		if (refresh !== previousRefresh.current) {
			previousRefresh.current = refresh;
			if (widget.enabled) {
				force.current = true;
				void refetch();
			}
		}
	}, [refresh, widget.enabled, refetch]);
	return (
		<>
			{exporting && (
				<ExportSettings id={widget.id} board={false} title={widget.title} onClose={() => setExporting(false)} />
			)}
			<header
				className={styles.widgetHeader}
				onPointerDown={(e) => {
					if (!(e.target as Element).closest("button")) onPointer(e, false);
				}}
			>
				<Button
					types={[ButtonTypes.Icon]}
					size="small"
					className={styles.grip}
					title="Drag to move; use arrow keys to move with keyboard"
					aria-label={`Move ${widget.title}`}
					onPointerDown={(e) => onPointer(e, false)}
					onKeyDown={(e) => onKey(e, false)}
				>
					<Grip size={18} />
				</Button>
				<div className={styles.widgetHeading}>
					<h2 title={widget.title}>{widget.title}</h2>
					<span>
						{query.isFetching
							? "Updating…"
							: query.dataUpdatedAt
								? `Updated ${new Date(query.dataUpdatedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
								: "Report"}
					</span>
				</div>
				<div className={styles.widgetDataActions} ref={setHeaderActions} />
				{!editing && (
					<Button
						types={[ButtonTypes.Icon]}
						size="small"
						title="Automated export"
						aria-label={`Automated export for ${widget.title}`}
						onClick={() => setExporting(true)}
					>
						<Mail size={16} />
					</Button>
				)}
				{!editing && (
					<Button
						types={[ButtonTypes.Icon]}
						size="small"
						title="Widget settings"
						aria-label={`Settings for ${widget.title}`}
						onClick={onConfigure}
					>
						<Settings2 size={16} />
					</Button>
				)}
				{!editing && (
					<Button
						types={[ButtonTypes.Icon]}
						size="small"
						title="Remove widget"
						aria-label={`Remove ${widget.title}`}
						onClick={onDelete}
					>
						<Trash2 size={15} />
					</Button>
				)}
			</header>
			<div className={styles.widgetContent} aria-busy={query.isFetching}>
				{!widget.enabled ? (
					<p className={commonStyles.emptyWidget}>This widget is no longer available for your account.</p>
				) : query.isPending ? (
					<div className={styles.skeleton} aria-label="Loading report">
						<Skeleton height={19} />
						<Skeleton height={19} width="75%" />
						<Skeleton height={19} width="88%" />
						<Skeleton height={19} />
					</div>
				) : (
					<>
						{query.isError && (
							<p className={commonStyles.error} role="alert">
								Could not refresh this report. <Button onClick={() => query.refetch()}>Retry</Button>
							</p>
						)}
						{query.data && (
							<WidgetDataView
								data={query.data.data}
								widgetName={widget.widgetName}
								configJson={widget.configJson}
								headerActions={headerActions}
								title={widget.title}
							/>
						)}
					</>
				)}
			</div>
			<Button
				types={[ButtonTypes.Icon]}
				size="small"
				className={styles.resize}
				title="Drag to resize; use arrow keys to resize with keyboard"
				aria-label={`Resize ${widget.title}`}
				onPointerDown={(e) => onPointer(e, true)}
				onKeyDown={(e) => onKey(e, true)}
			>
				<Maximize2 size={15} />
			</Button>
		</>
	);
}
